<template>
  <BaseDialog
    :title="displayText.title"
    width="45%"
    :btn1="displayText.btn.upload"
    :btn2="displayText.btn.cancel"
    :btn1Loading="uploading"
    :closeOnClickModal="false"
    @close="closeDialog"
    @confirm="upload"
  >
    <div style="margin-bottom: 40px;">
      <div class="cropper-wrapper">
        <Cropper
          ref="cropperRef"
          :src="imageBlob"
          :debounce="false"
          :stencil-props="stencilProps"
          @change="onChange"
          @ready="onCropperReady"
        />
      </div>

      <div v-if="result.image && preview" class="preview-wrapper">
        <h3>預覽</h3>
        <div class="flex border preview-content-wrapper">
          <Preview
            :width="previewWidth"
            :height="100"
            :image="result.image"
            :coordinates="result.coordinates"
          />
        </div>
      </div>

      <div class="text-sm text-gray-60">
        <p>原始檔案大小：<span :class="displayData.originSize.color">{{ displayData.originSize.size }}</span> </p>
        <div class="flex items-start justify-between">
          <div>
            <p>裁切後檔案大小：<span :class="loading.compress ? '' : displayData.cropSize.color">{{ loading.compress? '計算中' : displayData.cropSize.size }}</span> </p>
            <p>壓縮後檔案大小：<span :class="loading.compress ? '' : displayData.compressSize.color">{{ loading.compress? '計算中' : displayData.compressSize.size }}</span> </p>
          </div>
          <BaseElButton plain :loading="loading.compress" @click="refreshCompressSize">刷新計算</BaseElButton>
        </div>

        <p class="text-sm text-danger" style="margin-top: 12px">注意：系統上傳圖片大小限制為 {{ formatFileSize(maxFileSizeBytes) }}，如超過會自動壓縮圖片到 {{ formatFileSize(maxFileSizeBytes) }} 以下，可能會些微影響畫質，敬請見諒</p>
        <p v-show="isLongUpload" class="text-warning" style="margin-top: 20px">上傳檔案較大，請稍後等待上傳完畢</p>
      </div>
    </div>
  </BaseDialog>
</template>

<script>
import { defineComponent, ref, reactive, computed } from 'vue'
import { Cropper, Preview } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { i18n } from '@/plugins/i18n/i18n'
import Compressor from 'compressorjs'
import { admin2 } from '@/api/instance'
import store from '@/store'
import { get } from 'lodash'

export default defineComponent({
  name: 'NewImageCropper',
  components: {
    Cropper,
    Preview,
    BaseDialog,
  },
  props: {
    img: Object,
    ratio: {
      type: Array,
      default: () => [1, 1],
    },
    fixedRatio: {
      type: Boolean,
      default: true,
    },
    preview: {
      type: Boolean,
      default: true,
    },
    /**
     * 客製化檔案大小限制 (支援 KB 和 MB 單位)
     * 預設 10MB
     * 數字單位為 B，例如 10000
     * 字串單位為 KB、MB、GB，例如 10MB、10GB
     */
    maxFileSize: {
      type: [Number, String],
      default: '10MB',
      validator: (value) => {
        if (typeof value === 'number') {
          return value > 0
        }
        if (typeof value === 'string') {
          const match = value.match(/^(\d+(?:\.\d+)?)\s*(KB|MB|GB)$/i)
          return match && parseFloat(match[1]) > 0
        }
        return false
      },
    },
    // 是否啟用自動壓縮
    enableCompression: {
      type: Boolean,
      default: true,
    },
    // 壓縮品質 (0-1)
    compressionQuality: {
      type: Number,
      default: 1,
      validator: (value) => value >= 0 && value <= 1,
    },
  },
  setup (props, { emit }) {
    const cropperRef = ref(null)
    // 使用 props 中的檔案大小限制
    const maxFileSizeBytes = computed(() => {
      if (typeof props.maxFileSize === 'string') {
        const match = props.maxFileSize.match(/^(\d+(?:\.\d+)?)\s*(KB|MB|GB)$/i)
        if (match) {
          const size = parseFloat(match[1])
          if (match[2] === 'KB') return size * 1024
          if (match[2] === 'MB') return size * 1024 * 1024
          if (match[2] === 'GB') return size * 1024 * 1024 * 1024
        }
      }
      return props.maxFileSize
    })
    const isLongUpload = ref(false)
    const result = reactive({
      coordinates: null,
      image: null,
    })
    const uploading = ref(false)
    const loading = reactive({
      upload: false,
      compress: false,
    })

    // 智能檔案大小格式化函數
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 B'

      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    // 檢查檔案大小是否超過限制
    const isFileSizeExceeded = (bytes) => {
      return bytes > maxFileSizeBytes.value
    }

    const displayText = computed(() => {
      const text = {
        title: i18n.t('common.dialog.uploadImage.title'),
        btn: {
          upload: i18n.t('common.button.upload.text'),
          cancel: i18n.t('common.button.cancel.text'),
        },
      }
      return text
    })

    const ctx = reactive({
      compressedBlob: null,
      originalBlob: null,
      cropBlob: null,
    })

    const displayData = computed(() => {
      const originSize = ctx.originalBlob
        ? formatFileSize(ctx.originalBlob.size)
        : '計算中'

      const compressSize = ctx.compressedBlob
        ? formatFileSize(ctx.compressedBlob.size)
        : '請點擊刷新計算'

      const cropSize = ctx.cropBlob
        ? formatFileSize(ctx.cropBlob.size)
        : '請點擊刷新計算'

      return {
        originSize: {
          size: originSize,
          color: isFileSizeExceeded(get(ctx, 'originalBlob.size', 0)) ? 'text-danger' : '',
        },
        compressSize: {
          size: compressSize,
          color: isFileSizeExceeded(get(ctx, 'compressedBlob.size', 0)) ? 'text-danger' : '',
        },
        cropSize: {
          size: cropSize,
          color: isFileSizeExceeded(get(ctx, 'cropBlob.size', 0)) ? 'text-danger' : '',
        },
      }
    })

    const imageBlob = computed(() => {
      return props.img?.blob || null
    })

    const stencilProps = computed(() => {
      if (props.fixedRatio) {
        return {
          aspectRatio: props.ratio[0] / props.ratio[1],
        }
      }
      return {
        aspectRatio: null,
      }
    })

    const previewWidth = computed(() => {
      if (!result.coordinates?.width || !result.coordinates?.height) {
        return 100 // 默認等於高度
      }

      // 計算比例 width:height = ?:100
      const ratio = result.coordinates.width / result.coordinates.height
      return Math.round(100 * ratio)
    })

    const onChange = ({ coordinates, image }) => {
      result.coordinates = coordinates
      result.image = image
      emit('change', { coordinates, image })
    }

    const closeDialog = () => {
      result.coordinates = null
      result.image = null
      emit('close')
    }

    const compressImage = async (quality = props.compressionQuality) => {
      if (!cropperRef.value || !props.enableCompression) return Promise.resolve(null)
      console.log('開始壓縮')

      return new Promise((resolve) => {
        const { canvas } = cropperRef.value.getResult()
        if (!canvas) return resolve(null)

        loading.compress = true
        canvas.toBlob((blob) => {
          ctx.cropBlob = blob

          const compressRecursively = (blob, quality) => {
            return new Promise((resolve) => {
              const compressorInstance = new Compressor(blob, {
                quality,
                convertSize: maxFileSizeBytes.value,
                success (compressedBlob) {
                  console.log('壓縮成功', quality, formatFileSize(compressedBlob.size), formatFileSize(maxFileSizeBytes.value), compressedBlob.size > maxFileSizeBytes.value)

                  if (compressedBlob.size > maxFileSizeBytes.value && quality > 0.01) {
                    compressRecursively(blob, quality - 0.01)
                      .then(result => resolve(result))
                  } else {
                    ctx.compressedBlob = compressedBlob
                    loading.compress = false
                    resolve(compressedBlob)
                  }
                },
                error (err) {
                  loading.compress = false
                  console.log('壓縮失敗', err)
                  resolve(null)
                },
              })
            })
          }

          compressRecursively(blob, quality)
            .then(result => resolve(result))
        }, 'image/png')
      })
    }

    const refreshCompressSize = async () => {
      compressImage()
    }

    const upload = async () => {
      uploading.value = true
      let uploadTimer = null

      // Set timer to show message if upload takes more than 2 seconds
      uploadTimer = setTimeout(() => {
        isLongUpload.value = true
        window.$message.info('上傳檔案較大，請稍後等待上傳完畢')
      }, 2000)

      const { canvas } = cropperRef.value.getResult()
      if (canvas) {
        canvas.toBlob(async (blob) => {
          // Only compress if file size exceeds maxFileSize and compression is enabled
          let finalBlob = blob
          if (blob.size > maxFileSizeBytes.value && props.enableCompression) {
            await compressImage(props.compressionQuality)
            finalBlob = ctx.compressedBlob
            console.log('執行壓縮完畢', finalBlob)
          }

          if (finalBlob) {
            const form = new FormData()

            let name = props.img.name.split('.')
            name.pop()
            name = name + '.png'
            form.append('file', finalBlob, name.toLowerCase())
            console.log('開始上傳圖片')
            const [res, errMsg] = await admin2({
              method: 'post',
              url: 'image/upload',
              headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${store.getters.token}`,
              },
              data: form,
            })

            // Clear the timer if it hasn't triggered yet
            if (uploadTimer) {
              clearTimeout(uploadTimer)
              uploadTimer = null
            }

            uploading.value = false

            if (errMsg) {
              console.log(`上傳圖片失敗：${errMsg}`)
              window.$message.error(`上傳圖片失敗：${errMsg}`)
              return
            }
            window.$message.success('上傳圖片成功 !')

            emit('uploaded', res)
            emit('done', { res, imgDataUrl: canvas.toDataURL() })
          }
        }, 'image/png')
      }
    }

    const setDefaultCoordinates = () => {
      const cropper = cropperRef.value
      const center = {
        left: cropper.coordinates.left + cropper.coordinates.width / 2,
        top: cropper.coordinates.top + cropper.coordinates.height / 2,
      }

      cropperRef.value.setCoordinates([
        ({ coordinates, imageSize }) => ({
          width: imageSize.width,
          height: imageSize.height,
        }),
        ({ coordinates, imageSize }) => ({
          left: center.left - coordinates.width / 2,
          top: center.top - coordinates.height / 2,
        }),
      ])
    }

    const onCropperReady = () => {
      if (!cropperRef.value) return
      const { canvas } = cropperRef.value.getResult()
      if (canvas) {
        canvas.toBlob(async (blob) => {
          console.log('原始檔案大小:', formatFileSize(blob.size))
          ctx.originalBlob = blob
        })
      }

      setDefaultCoordinates()
    }

    return {
      imageBlob,
      result,
      onChange,
      cropperRef,
      closeDialog,
      upload,
      uploading,
      displayText,
      stencilProps,
      previewWidth,
      displayData,
      onCropperReady,
      refreshCompressSize,
      loading,
      isLongUpload,
      maxFileSizeBytes,
      formatFileSize,
      isFileSizeExceeded,
    }
  },
})
</script>

<style scoped lang="postcss">

.cropper-wrapper {
  @apply w-full h-[250px];
}

.preview-wrapper {
  @apply flex flex-col items-center gap-2.5 my-4 mx-auto;
}

.preview-wrapper h3 {
  @apply m-0;
}

.preview-btn {
  @apply mt-4 mb-0;
}

.notice {
  @apply text-right text-xs text-[#d8749c];
}

.preview-content-wrapper {
  @apply h-[100px] max-w-full overflow-hidden;
}
</style>
